import React, { FC } from "react";

import { Box, Slider, Stack } from "@mui/material";

import { TPlayerProps } from "@components/BottomPlayer/components/types";
import * as Styled from "@components/BottomPlayer/styles";
import { ERoutes } from "@utils/constants";
import styled, { keyframes } from "styled-components";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { colors } from "@/styles/design-system";

import { getSkeleton, MAX_VOLUME, TSkeletons } from "../../utils";

// -- Play button --
const PlayBtn = styled.button<{ $playing?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $playing }) => ($playing ? "#ffdca8" : "transparent")};
  border: 1.5px solid ${({ $playing }) => ($playing ? "#ffdca8" : "rgba(255, 255, 255, 0.2)")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $playing }) => ($playing ? "#5a3d2a" : "rgba(255, 255, 255, 0.6)")};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #ffdca8;
    color: ${({ $playing }) => ($playing ? "#5a3d2a" : "#ffdca8")};
    background: ${({ $playing }) => ($playing ? "#ffdca8" : "rgba(255, 220, 168, 0.06)")};
    ${({ $playing }) => $playing && "box-shadow: 0 0 12px rgba(255, 220, 168, 0.4);"}
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
    stroke: currentColor;
    stroke-width: 1.5;
  }
`;

// -- Heart button --
const HeartBtn = styled.button<{ $active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #ffffff;

  svg {
    width: 18px;
    height: 18px;
    fill: ${({ $active }) => ($active ? "#ffffff" : "transparent")};
    stroke: #ffffff;
    stroke-width: 1.5;
    transition: all 0.2s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    svg { fill: #ffffff; }
  }
`;

// -- Action button (circular, gold gradient on hover) --
const ActionBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  color: ${colors.accent.blue};
  transition: all 0.2s ease;
  z-index: 0;

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: transparent;
    stroke-width: 1.5;
    position: relative;
    z-index: 1;
    transition: stroke 0.2s ease;
  }

  /* Solid blue reveal on hover — matches the /music track action button. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: #9cd3fd;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 0;
  }

  &:hover::before { opacity: 1; }
  &:hover {
    box-shadow: 0 0 12px rgba(156, 211, 253, 0.6);
    svg { stroke: #0a1a2a; }
  }
`;

// -- Volume (horizontal, boxed) --
const VolumeInline = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  padding: 6px 14px;

  .vol-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #ffffff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    svg { width: 100%; height: 100%; }
  }

  .MuiSlider-root {
    width: 80px;
    height: 2px;
    padding: 6px 0;
    color: #e0d2c1;
    opacity: ${({ $muted }) => ($muted ? 0.5 : 1)};
    transition: opacity 0.2s ease;
  }
  .MuiSlider-track { background: #e0d2c1; border: none; height: 2px; }
  .MuiSlider-rail { background: rgba(255, 255, 255, 0.25); height: 2px; }
  .MuiSlider-thumb {
    width: 12px; height: 12px;
    background: #e0d2c1;
    box-shadow: none;
    &:hover, &.Mui-focusVisible { box-shadow: 0 0 8px rgba(224, 210, 193, 0.4); }
  }
`;

// -- Typography --
const TrackTitle = styled.span`
  color: #fbf4ec;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  &:hover { text-decoration: underline; }
`;

const ComposerName = styled.span`
  color: #efd0b8;
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.2;
  cursor: pointer;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  &:hover { text-decoration: underline; }
`;

const TimeLabel = styled.span`
  color: rgba(251, 244, 236, 0.5);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.8125rem;
  white-space: nowrap;
  min-width: 36px;
  text-align: center;
`;

const bottomPlayerCoverFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Cover = styled.img`
  border-radius: 8px;
  width: 48px;
  height: 48px;
  object-fit: cover;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  /* 700ms fade-in on mount — runs regardless of cache state via CSS animation
     (the onLoad-based approach didn't show a visible fade for already-cached
     thumbnails because the load completes synchronously). */
  animation: ${bottomPlayerCoverFadeIn} 700ms ease both;
  &:hover { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3); }
`;

// -- Card layout (G design) --
const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0;

  .bp-left { padding-right: 20px; }
  .bp-center {
    padding: 0 20px;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    position: relative;
  }
  .bp-right { padding-left: 20px; gap: 6px; }
  .bp-volume {
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    padding-left: 16px;
    margin-left: 6px;
  }
`;

// -- Close button (top-right corner of pill) --
const CloseBtn = styled.button`
  position: absolute;
  top: -14px;
  right: -14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgb(100, 75, 55);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  svg {
    width: 14px;
    height: 14px;
    stroke: #ffffff;
    stroke-width: 2.5;
    stroke-linecap: round;
    transition: all 0.25s ease;
  }

  &:hover {
    background: rgba(100, 75, 55, 0.9);
    transform: scale(1.1);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
    svg { stroke: #fbf4ec; }
  }

  &:active {
    transform: scale(0.95);
  }
`;

// -- Background overlay (creamy pill) --
const DesignOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

export const DesktopLayout: FC<TPlayerProps> = ({
  state,
  bottomTrackData,
  currentTimeRef,
  handleNavigate,
  handleLicensing: _handleLicensing,
  handleNavigateComposer,
  handleDownload,
  handleVolumeMute,
  isVolumeTouched: _isVolumeTouched,
  onVolumeChange,
  onPlayClick,
  containerRef,
  dispatchReducer: _dispatchReducer,
  handleLike,
  handleCloseTrack,
  handleSeek: _handleSeek,
}) => {
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();
  const trackTier = (bottomTrackData?.tier || "premium") as "free" | "premium";
  // Premium tracks always show Buy for Free users (re-buyable licenses); owning
  // one doesn't flip to Download — downloads live on the orders page.
  const showDownloadIcon =
    accountType === AccountType.SUBSCRIPTION ||
    accountType === AccountType.MUSIC_CREATOR ||
    (accountType === AccountType.FREE && trackTier === "free");

  const composerDisplay = state.isLoading
    ? null
    : bottomTrackData?.composer
      ? [typeof bottomTrackData.composer === "object" ? (bottomTrackData.composer as any).name : bottomTrackData.composer]
      : [];

  return (
    <>
      <DesignOverlay />

      <CloseBtn onClick={handleCloseTrack}>
        <svg viewBox="0 0 24 24" fill="none">
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </CloseBtn>

      <ContentWrapper>
        {/* LEFT: Cover + Info */}
        <Stack className="bp-left" direction="row" alignItems="center" spacing="14px" sx={{ flexShrink: 0, minWidth: 0, maxWidth: 260 }}>
          {state.isLoading ? (
            getSkeleton(TSkeletons.AlbumCover, true)
          ) : (
            <Cover
              key={bottomTrackData?.smallCoverUrl || bottomTrackData?.largeCoverUrl}
              onClick={() => handleNavigate(ERoutes.Album, bottomTrackData?.album.slug, true)}
              alt="Album cover"
              src={bottomTrackData?.smallCoverUrl || bottomTrackData?.largeCoverUrl}
            />
          )}
          <Stack spacing="3px" sx={{ minWidth: 0, overflow: "hidden" }}>
            <TrackTitle className="song-title" onClick={() => handleNavigate(ERoutes.Track, bottomTrackData?.slug)}>
              {state.isLoading ? getSkeleton(TSkeletons.TITLE) : bottomTrackData?.name}
            </TrackTitle>
            <ComposerName onClick={() => handleNavigateComposer(typeof bottomTrackData?.composer === "object" ? (bottomTrackData?.composer as any)?.name : bottomTrackData?.composer as string)}>
              {state.isLoading ? getSkeleton(TSkeletons.COMPOSER) : composerDisplay}
            </ComposerName>
          </Stack>
        </Stack>

        {/* CENTER: Play + Time + Waveform */}
        <Stack className="bp-center" direction="row" spacing="10px" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {state.isLoading ? (
            getSkeleton(TSkeletons.PlayButton, true)
          ) : (
            <PlayBtn $playing={state.isPlaying} onClick={onPlayClick}>
              {state.isPlaying ? (
                <svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" fill="currentColor" /><rect x="14" y="4" width="4" height="16" fill="currentColor" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" style={{ transform: "translateX(1px)" }}><polygon points="5 3 19 12 5 21 5 3" /></svg>
              )}
            </PlayBtn>
          )}

          <TimeLabel ref={currentTimeRef as React.Ref<HTMLSpanElement>}>
            {state.isLoading ? getSkeleton(TSkeletons.TIME) : "00:00"}
          </TimeLabel>

          <Styled.WaveFormStack width="100%" $isCurrent sx={{ flex: 1, minWidth: 0 }}>
            {state.isLoading && getSkeleton(TSkeletons.LineProgress, true)}
            <Box id="waveform" ref={containerRef} width="100%" height="100%" sx={{ display: state.isLoading ? "none" : "block", fontFamily: '"Raleway", sans-serif !important' }} />
          </Styled.WaveFormStack>
        </Stack>

        {/* RIGHT: Heart + Action + Volume */}
        <Stack className="bp-right" direction="row" spacing="10px" alignItems="center" sx={{ flexShrink: 0 }}>
          <HeartBtn $active={state.isFavorite} onClick={handleLike}>
            <svg viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
          </HeartBtn>

          <ActionBtn onClick={handleDownload}>
            {showDownloadIcon ? (
              <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            ) : (
              <svg viewBox="0 0 24 24"><circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" /></svg>
            )}
          </ActionBtn>

          <span className="bp-volume">
            <VolumeInline $muted={state.isMuted}>
              <span className="vol-icon" onClick={handleVolumeMute}>
                {state.isMuted ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </span>
              <Slider value={state.isMuted ? 0 : state.volume} min={0} max={MAX_VOLUME} step={0.05} onChange={onVolumeChange} />
            </VolumeInline>
          </span>
        </Stack>


      </ContentWrapper>
    </>
  );
};
